// =====================================================
// ProfileHub.jsx signed-in GoOurHub profile
//
// Shows:
//   * Favorite communities
//   * Saved businesses
//   * Coming-soon profile features
// =====================================================

import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  Link,
  useNavigate,
} from "react-router-dom";



import {
  api,
  useSession,
  clearSession,
  SIGN_IN_ROUTE,
} from "./session";

import {
  useFavorites,
} from "./FavoritesContext";

import {
  useCommunityFavorites,
} from "./CommunityFavorites";

import "./Favorites.css";
import SavedEventsCard from "./SavedEventsCard";
import {
  FaHeart,
  FaRegHeart,
  FaStar,
  FaTag,
  FaBell,
  FaSignOutAlt,
  FaChevronRight,
  FaMapMarkerAlt,
  FaUsers,
  FaTrash,
} from "react-icons/fa";
const HUBMART_API =
  "https://api.goourhub.com:435";

const HUBMART_IMAGE_CDN =
  "https://goourhub.sfo2.digitaloceanspaces.com/img/";

const HUBMART_CATEGORIES = {
  0: "Free",
  1: "Vehicles",
  2: "Mobile",
  3: "Computers",
  4: "Jobs",
  5: "Clothing",
  6: "Rentals",
  7: "Homes",
  8: "Toys",
  9: "Books",
  10: "Other",
};

function getHubMartId(item) {
  const raw =
    item?._id ||
    item?.id ||
    item?.postid ||
    "";

  if (
    raw &&
    typeof raw === "object"
  ) {
    return String(
      raw.$oid ||
        raw.oid ||
        raw.id ||
        ""
    );
  }

  return String(raw || "");
}

function getHubMartImage(item) {
  const first =
    Array.isArray(item?.images) &&
    item.images.length
      ? item.images[0]
      : item?.image || "";

  if (!first) return "";

  if (
    /^https?:\/\//i.test(first)
  ) {
    return first;
  }

  return (
    HUBMART_IMAGE_CDN +
    String(first).replace(/^\/+/, "")
  );
}

function hubMartPrice(value) {
  const number = Number(value || 0);

  if (
    !Number.isFinite(number) ||
    number <= 0
  ) {
    return "Free";
  }

  return `$${number.toFixed(2)}`;
}

export default function ProfileHub() {
  const user = useSession();
  const navigate = useNavigate();

  /* Universal business details card for Saved places   same
     card-first behavior as the hub and community pages. */
  const [selectedPlace, setSelectedPlace] = useState(null);

  useEffect(() => {
    if (!selectedPlace) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setSelectedPlace(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectedPlace]);

  const openPlaceMap = (place) => {
    const query = [place?.name, place?.city || "San Antonio"]
      .filter(Boolean)
      .join(" ");
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
      "_blank",
      "noopener"
    );
  };

  const sharePlace = async (place) => {
    const text = `${place?.name || "A local business"} on GoOurHub`;
    const url = window.location.origin;
    try {
      if (navigator.share) {
        await navigator.share({ title: text, text, url });
        return;
      }
    } catch {
      return;
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      window.alert("Link copied.");
    } catch {
      window.alert(url);
    }
  };

  const {
    isSaved,
    toggleSave,
    savedCount,
  } = useFavorites();

  const {
    favorites: favoriteCommunities,
    favoriteCount: favoriteCommunityCount,
    toggleCommunityFavorite,
  } = useCommunityFavorites();

  const [savedPlaces, setSavedPlaces] =
    useState([]);

  const [loadState, setLoadState] =
    useState("idle");


    const [
  hubMartListings,
  setHubMartListings,
] = useState([]);

const [
  hubMartLoadState,
  setHubMartLoadState,
] = useState("idle");
const [
  deletingHubMartId,
  setDeletingHubMartId,
] = useState("");

const [
  hubMartDeleteMessage,
  setHubMartDeleteMessage,
] = useState("");
  const loadSaved = useCallback(
    async () => {
      if (!user) {
        return;
      }

      setLoadState("loading");

      try {
        const response =
          await api.get(
            "/api/favorites"
          );

        setSavedPlaces(
          response?.data?.favorites ||
            []
        );

        setLoadState("done");
      } catch {
        setLoadState("error");
      }
    },
    [user]
  );

  const loadHubMartListings =
  useCallback(async () => {
    if (!user?.email) {
      setHubMartListings([]);
      return;
    }

    setHubMartLoadState("loading");
const handleDeleteHubMartListing =
  async (listing) => {
    const id =
      getHubMartId(listing);

    const categoryId =
      Number(listing?.categoryId);

    if (
      !id ||
      !Number.isInteger(categoryId)
    ) {
      setHubMartDeleteMessage(
        "This listing could not be identified."
      );
      return;
    }

    if (!user?.token) {
      navigate(SIGN_IN_ROUTE);
      return;
    }

    const title =
      listing?.title ||
      "this listing";

    const confirmed =
      window.confirm(
        `Delete "${title}"?\n\nThis will permanently remove the listing and its uploaded photos.`
      );

    if (!confirmed) {
      return;
    }

    setDeletingHubMartId(id);
    setHubMartDeleteMessage("");

    try {
      const params =
        new URLSearchParams({
          id,
          category:
            String(categoryId),
          ct: "1",
        });

      const response =
        await fetch(
          `${HUBMART_API}/delete?${params.toString()}`,
          {
            method: "DELETE",
            headers: {
              Authorization:
                `Bearer ${user.token}`,
            },
          }
        );

      const raw =
        await response.text();

      let body = null;

      try {
        body =
          raw
            ? JSON.parse(raw)
            : null;
      } catch {
        body = null;
      }

      if (!response.ok) {
        const message =
          body?.message ||
          raw ||
          `Delete failed: ${response.status}`;

        throw new Error(message);
      }

      setHubMartListings(
        (current) =>
          current.filter(
            (item) =>
              getHubMartId(item) !==
              id
          )
      );

      setHubMartDeleteMessage(
        `"${title}" was deleted successfully.`
      );
    } catch (error) {
      setHubMartDeleteMessage(
        error?.message ||
          "Could not delete the listing."
      );
    } finally {
      setDeletingHubMartId("");
    }
  };
    const ownerEmail =
      user.email
        .trim()
        .toLowerCase();

    try {
      const requests =
        Object.entries(
          HUBMART_CATEGORIES
        ).map(
          async ([
            categoryId,
            categoryLabel,
          ]) => {
            const params =
  new URLSearchParams({
    category:
      String(categoryId),
    ct: "1",
    limit: "200",
  });

            const response =
              await fetch(
                `${HUBMART_API}/search?${params.toString()}`
              );

            // Existing search API returns
            // 404 when that category has
            // no matching listings.
            if (
              response.status === 404
            ) {
              return [];
            }

            if (!response.ok) {
              throw new Error(
                `HubMart search failed: ${response.status}`
              );
            }

            const body =
              await response.json();

            const rows =
              Array.isArray(body)
                ? body
                : body?.results ||
                  body?.result ||
                  body?.items ||
                  [];

            return rows
              .filter((item) => {
                const itemEmail =
                  String(
                    item?.email ||
                      item?.contactEmail ||
                      ""
                  )
                    .trim()
                    .toLowerCase();

                return (
                  itemEmail ===
                    ownerEmail &&
                  item?.verified ===
                    true
                );
              })
              .map((item) => ({
                ...item,
                categoryId:
                  Number(
                    categoryId
                  ),
                categoryLabel,
              }));
          }
        );

      const chunks =
        await Promise.all(
          requests
        );

      const seen = new Set();

      const listings =
        chunks
          .flat()
          .filter((item) => {
            const id =
              getHubMartId(item);

            if (
              !id ||
              seen.has(id)
            ) {
              return false;
            }

            seen.add(id);
            return true;
          })
          .sort((a, b) =>
            getHubMartId(b).localeCompare(
              getHubMartId(a)
            )
          );

      setHubMartListings(
        listings
      );

      setHubMartLoadState(
        "done"
      );
    } catch (error) {
      console.error(
        "Could not load HubMart listings:",
        error
      );

      setHubMartLoadState(
        "error"
      );
    }
  }, [user?.email]);

const handleDeleteHubMartListing =
  async (listing) => {
    const id =
      getHubMartId(listing);

    const categoryId =
      Number(listing?.categoryId);

    if (
      !id ||
      !Number.isInteger(categoryId)
    ) {
      setHubMartDeleteMessage(
        "This listing could not be identified."
      );
      return;
    }

    if (!user?.token) {
      navigate(SIGN_IN_ROUTE);
      return;
    }

    const title =
      listing?.title ||
      "this listing";

    const confirmed =
      window.confirm(
        `Delete "${title}"?\n\nThis will permanently remove the listing and its uploaded photos.`
      );

    if (!confirmed) {
      return;
    }

    setDeletingHubMartId(id);
    setHubMartDeleteMessage("");

    try {
      const params =
        new URLSearchParams({
          id,
          category:
            String(categoryId),
          ct: "1",
        });

      const response =
        await fetch(
          `${HUBMART_API}/delete?${params.toString()}`,
          {
            method: "DELETE",
            headers: {
              Authorization:
                `Bearer ${user.token}`,
            },
          }
        );

      const raw =
        await response.text();

      let body = null;

      try {
        body =
          raw
            ? JSON.parse(raw)
            : null;
      } catch {
        body = null;
      }

      if (!response.ok) {
        const message =
          body?.message ||
          raw ||
          `Delete failed: ${response.status}`;

        throw new Error(message);
      }

      setHubMartListings(
        (current) =>
          current.filter(
            (item) =>
              getHubMartId(item) !== id
          )
      );

      setHubMartDeleteMessage(
        `"${title}" was deleted successfully.`
      );
    } catch (error) {
      setHubMartDeleteMessage(
        error?.message ||
          "Could not delete the listing."
      );
    } finally {
      setDeletingHubMartId("");
    }
  };


  useEffect(() => {
    loadSaved();
  }, [
    loadSaved,
    savedCount,
  ]);
  useEffect(() => {
  loadHubMartListings();
}, [loadHubMartListings]);

  const handleSignOut = () => {
    clearSession();
    navigate("/");
  };

  /* ---------- guest view ---------- */

  if (!user) {
    return (
      <main className="ghfav-profile">
        <section className="ghfav-card ghfav-card--center">
          <div
            className="ghfav-hello-icon"
            aria-hidden="true"
          >
            <FaRegHeart />
          </div>

          <h1>
            Your GoOurHub profile
          </h1>

          <p className="ghfav-muted">
            Sign in to save favorite
            communities and businesses,
            and soon: write reviews,
            follow businesses, and claim
            local deals.
          </p>

          <Link
            to={SIGN_IN_ROUTE}
            className="ghfav-cta ghfav-cta--primary ghfav-signin-link"
          >
            Sign in / create account
          </Link>
        </section>
      </main>
    );
  }

  /* ---------- signed-in view ---------- */

  const displayName =
    user.firstName ||
    user.email;

  return (
    <main className="ghfav-profile">
      <header className="ghfav-profile-hero">
        <span className="ghfav-kicker">
          My GoOurHub
        </span>

        <h1>
          Hi {displayName}{" "}
          <span aria-hidden="true">
            👋
          </span>
        </h1>

        <p className="ghfav-muted">
          {user.email}
        </p>
      </header>

      {/* ---------- Favorite communities ---------- */}

      <section className="ghfav-card ghfav-saved">
        <div className="ghfav-card__head">
          {hubMartDeleteMessage && (
  <p className="ghfav-muted">
    {hubMartDeleteMessage}
  </p>
)}
          <h2>
            <FaUsers aria-hidden="true" />{" "}
            Favorite communities
          </h2>

          {favoriteCommunityCount >
            0 && (
            <span className="ghfav-count">
              {favoriteCommunityCount}
            </span>
          )}
        </div>

        {favoriteCommunities.length ===
          0 && (
          <p className="ghfav-muted">
            No favorite communities yet.
            Tap the ♥ on a community in
            the Hub and it will show up
            here.
          </p>
        )}

        {favoriteCommunities.length >
          0 && (
          <ul className="ghfav-list">
            {favoriteCommunities.map(
              (community) => {
                const name =
                  community.people ||
                  "Community";

                return (
                  <li
                    key={community.id}
                    className="ghfav-item"
                  >
                    {community.url ? (
                      <Link
                        to={
                          community.url
                        }
                        className="ghfav-item__body"
                      >
                        <span className="ghfav-item__name">
                          {community.flag && (
                            <>
                              <span
                                className={`fi fi-${community.flag}`}
                                aria-hidden="true"
                                style={{
                                  marginRight:
                                    8,
                                }}
                              />
                            </>
                          )}
                          {name}
                        </span>

                        <span className="ghfav-item__meta">
                          Open community
                        </span>
                      </Link>
                    ) : (
                      <div className="ghfav-item__body">
                        <span className="ghfav-item__name">
                          {name}
                        </span>
                      </div>
                    )}

                    <button
                      type="button"
                      className="ghfav-btn ghfav-btn--round is-saved"
                      onClick={() =>
                        toggleCommunityFavorite(
                          community
                        )
                      }
                      aria-pressed="true"
                      aria-label={`Remove ${name} from favorite communities`}
                      title="Remove favorite community"
                    >
                      <FaHeart
                        aria-hidden="true"
                      />
                    </button>
                  </li>
                );
              }
            )}
          </ul>
        )}
      </section>

      {/* ---------- Saved businesses ---------- */}

      <section className="ghfav-card ghfav-saved">
        <div className="ghfav-card__head">
          <h2>
            <FaHeart aria-hidden="true" />{" "}
            Saved places
          </h2>

          {savedCount > 0 && (
            <span className="ghfav-count">
              {savedCount}
            </span>
          )}
        </div>

        {loadState ===
          "loading" && (
          <p className="ghfav-muted">
            Loading your places…
          </p>
        )}

        {loadState ===
          "error" && (
          <p className="ghfav-muted">
            Couldn&rsquo;t load saved
            places right now.{" "}
            <button
              type="button"
              className="ghfav-retry"
              onClick={loadSaved}
            >
              Try again
            </button>
          </p>
        )}

        {loadState === "done" &&
          savedPlaces.length ===
            0 && (
            <p className="ghfav-muted">
              Nothing saved yet. Tap the
              ♥ on any business and it
              will show up here.
            </p>
          )}

        {savedPlaces.length >
          0 && (
          <ul className="ghfav-list">
            {savedPlaces.map(
              (place) => {
                const id = String(
                  place.businessId ??
                    place.business_id ??
                    ""
                );

                const name =
                  place.name ||
                  `Business ${id}`;

                const meta = [
                  place.category,
                  place.city,
                ]
                  .filter(Boolean)
                  .join(" · ");

                return (
                  <li
                    key={id}
                    className="ghfav-item"
                  >
                    <button
                      type="button"
                      className="ghfav-item__body gh-tap"
                      onClick={() =>
                        setSelectedPlace(place)
                      }
                    >
                      <span className="ghfav-item__name">
                        {name}
                      </span>

                      {meta && (
                        <span className="ghfav-item__meta">
                          <FaMapMarkerAlt
                            aria-hidden="true"
                          />{" "}
                          {meta}
                        </span>
                      )}
                    </button>

                    <button
                      type="button"
                      className={`ghfav-btn ghfav-btn--round ${
                        isSaved(id)
                          ? "is-saved"
                          : ""
                      }`}
                      onClick={() =>
                        toggleSave(
                          id,
                          name
                        )
                      }
                      aria-pressed={
                        isSaved(id)
                      }
                      aria-label={`Remove ${name} from saved`}
                    >
                      {isSaved(id) ? (
                        <FaHeart
                          aria-hidden="true"
                        />
                      ) : (
                        <FaRegHeart
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  </li>
                );
              }
            )}
          </ul>
        )}
      </section>
      {/* ---------- Saved events ---------- */}

      <SavedEventsCard />

      {selectedPlace && (
        <div
          className="ci-sheetwrap"
          onClick={() => setSelectedPlace(null)}
        >
          <div
            className="ci-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={selectedPlace.name || "Business details"}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="ci-sheet__x"
              onClick={() => setSelectedPlace(null)}
              aria-label="Close"
            >
              ✕
            </button>

            <h3 className="ci-sheet__nm">
              {selectedPlace.name || "Business"}
            </h3>

            {(selectedPlace.category || selectedPlace.city) && (
              <p className="ci-sheet__row">
                {[selectedPlace.category, selectedPlace.city]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            )}

            <button
              type="button"
              className="ci-sheet__takeme"
              onClick={() => openPlaceMap(selectedPlace)}
            >
              <FaMapMarkerAlt /> Take Me There
            </button>

            <div className="ci-sheet__acts">
              <button
                type="button"
                className="ci-sheet__act is-on"
                onClick={() =>
                  toggleSave(
                    String(
                      selectedPlace.id ?? selectedPlace.business_id ?? ""
                    ),
                    selectedPlace.name
                  )
                }
              >
                <FaHeart /> Saved
              </button>

              <button
                type="button"
                className="ci-sheet__act"
                onClick={() => sharePlace(selectedPlace)}
              >
                Share
              </button>

              <button
                type="button"
                className="ci-sheet__act"
                onClick={() => setSelectedPlace(null)}
              >
                Close
              </button>
            </div>

            <p className="ci-sheet__disc">
              Saved from your favorites. Open the business's community or
              category page for the full listing.
            </p>
          </div>
        </div>
      )}

      {/* ---------- My HubMart listings ---------- */}

<section className="ghfav-card ghfav-saved">
  <div className="ghfav-card__head">
    <h2>
      <FaTag aria-hidden="true" />{" "}
      My HubMart listings
    </h2>

    {hubMartListings.length >
      0 && (
      <span className="ghfav-count">
        {hubMartListings.length}
      </span>
    )}
  </div>

  {hubMartLoadState ===
    "loading" && (
    <p className="ghfav-muted">
      Loading your HubMart
      listings…
    </p>
  )}

  {hubMartLoadState ===
    "error" && (
    <p className="ghfav-muted">
      Couldn&rsquo;t load your
      HubMart listings right now.{" "}
      <button
        type="button"
        className="ghfav-retry"
        onClick={
          loadHubMartListings
        }
      >
        Try again
      </button>
    </p>
  )}

  {hubMartLoadState ===
    "done" &&
    hubMartListings.length ===
      0 && (
      <p className="ghfav-muted">
        You don&rsquo;t have any
        published HubMart listings
        yet.
      </p>
    )}

  {hubMartListings.length >
    0 && (
    <ul className="ghfav-list">
      {hubMartListings.map(
        (listing) => {
          const id =
            getHubMartId(
              listing
            );

          const image =
            getHubMartImage(
              listing
            );

          const meta = [
            listing.categoryLabel,
            hubMartPrice(
              listing.price
            ),
            listing.location,
          ]
            .filter(Boolean)
            .join(" · ");

         return (
  <li
    key={id}
    className="ghfav-item"
  >
    {image && (
      <img
        src={image}
        alt={listing.title || ""}
        style={{
          width: 72,
          height: 72,
          objectFit: "cover",
          borderRadius: 12,
          flexShrink: 0,
        }}
      />
    )}

    <div className="ghfav-item__body">
      <span className="ghfav-item__name">
        {listing.title ||
          "HubMart listing"}
      </span>

      <span className="ghfav-item__meta">
        {meta}
      </span>

      <span className="ghfav-item__meta">
        Published
      </span>
    </div>

    <button
      type="button"
      className="ghfav-btn ghfav-btn--round"
      onClick={() =>
        handleDeleteHubMartListing(
          listing
        )
      }
      disabled={
        deletingHubMartId === id
      }
      aria-label={`Delete ${
        listing.title ||
        "HubMart listing"
      }`}
      title="Delete listing"
      style={{
        color: "#b42318",
      }}
    >
      <FaTrash aria-hidden="true" />
    </button>
  </li>
);        }
      )}
    </ul>
  )}
</section>

      {/* ---------- Coming soon ---------- */}

      <section className="ghfav-grid">
        <div
          className="ghfav-tile"
          aria-disabled="true"
        >
          <FaStar
            aria-hidden="true"
          />
          <strong>
            My reviews
          </strong>
          <span>
            Coming soon
          </span>
        </div>

        <div
          className="ghfav-tile"
          aria-disabled="true"
        >
          <FaTag
            aria-hidden="true"
          />
          <strong>
            My deals
          </strong>
          <span>
            Coming soon
          </span>
        </div>

        <div
          className="ghfav-tile"
          aria-disabled="true"
        >
          <FaBell
            aria-hidden="true"
          />
          <strong>
            Notifications
          </strong>
          <span>
            Coming soon
          </span>
        </div>
      </section>

      <button
        type="button"
        className="ghfav-signout"
        onClick={handleSignOut}
      >
        <FaSignOutAlt
          aria-hidden="true"
        />{" "}
        Sign out

        <FaChevronRight
          className="ghfav-signout__chev"
          aria-hidden="true"
        />
      </button>
    </main>
  );
}
